import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <section className="pt-32 pb-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">返回首页</Link>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            image: article.imageUrl,
            publisher: {
              '@type': 'Organization',
              name: '一起草',
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <article className="pt-24 pb-20">
          <div className="w-full px-6 md:px-10 max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{article.category}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-medium text-primary-500 bg-primary-100 px-3 py-1 rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
                <span className="text-xs text-foreground-400">{article.date}</span>
              </div>
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                  <i className="ri-user-line text-foreground-600 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-foreground-700">{article.author}</span>
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden h-[300px] md:h-[400px]">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
                width={800}
                height={450}
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-lg md:prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-5
                prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-5
                prose-strong:text-foreground-900
                prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium text-secondary-600 bg-secondary-100 px-3 py-1.5 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回「{article.category}」分类
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}